import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

interface FunnelStepProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  onNext: () => void;
  onPrev?: () => void;
  nextText?: string;
  nextDisabled?: boolean;
  isLastStep?: boolean;
  className?: string;
}

const FunnelStep = ({
  title,
  subtitle,
  children,
  onNext,
  onPrev,
  nextText,
  nextDisabled = false,
  isLastStep = false,
  className
}: FunnelStepProps) => {
  const { t } = useLanguage();
  
  return (
    <div className={cn(
      "animate-fade-in space-y-6 max-w-2xl mx-auto min-h-[400px] flex flex-col justify-center",
      className
    )}>
      <div className="text-center space-y-3">
        <h2 className="text-3xl md:text-4xl font-black leading-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            {subtitle}
          </p>
        )}
      </div>

      <div className="space-y-6">
        {children}
      </div>

      <div className="flex justify-between items-center pt-6">
        {onPrev ? (
          <Button
            variant="ghost"
            onClick={onPrev}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('funnel.buttons.back')}
          </Button>
        ) : (
          <div /> 
        )}

        <Button
          onClick={onNext}
          disabled={nextDisabled}
          className={cn(
            "hero-button group !px-3 !py-2 sm:!px-6 sm:!py-3 !text-xs sm:!text-base md:!text-lg max-w-full text-center !leading-tight break-words",
            isLastStep && "!bg-gradient-to-r !from-green-500 !to-green-600 hover:!from-green-600 hover:!to-green-700"
          )}
        >
          <span className="truncate">{nextText || t('funnel.buttons.continue')}</span>
          <ArrowRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform flex-shrink-0" />
        </Button>
      </div>
    </div>
  );
};

// Helper components for common input types
export const OptionButton = ({ 
  selected, 
  onClick, 
  children, 
  className 
}: { 
  selected: boolean; 
  onClick: () => void; 
  children: ReactNode; 
  className?: string;
}) => (
  <button
    onClick={onClick}
    className={cn(
      "w-full p-4 text-left border-2 rounded-lg transition-all duration-300 hover:scale-[1.02]",
      selected 
        ? "border-primary bg-primary/10 text-primary shadow-lg shadow-primary/20" 
        : "border-border bg-card hover:border-primary/50 hover:bg-card/80",
      className
    )}
  >
    {children}
  </button>
);

export const StepInput = ({ 
  label, 
  value, 
  onChange, 
  placeholder, 
  required = false,
  type = "text"
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  type?: string;
}) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-foreground">
      {label} {required && <span className="text-destructive">*</span>}
    </label>
    <Input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="text-lg p-4 h-auto border-2 focus:border-primary transition-colors"
      required={required}
    />
  </div>
);

export const StepTextarea = ({ 
  label, 
  value, 
  onChange, 
  placeholder, 
  required = false 
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
}) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-foreground">
      {label} {required && <span className="text-destructive">*</span>}
    </label>
    <Textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="text-lg p-4 min-h-32 border-2 focus:border-primary transition-colors resize-none"
      required={required}
    />
  </div>
);

export default FunnelStep;